import { useEffect, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, ContainerActions, Dropdown } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type ContactsTableStore } from 'pages/contacts/ui/ContactsTable'
import { ContactsFiltersHeader } from 'pages/contacts/ui/ContactsMenu/ui/ContactsFiltersHeader/ContactsFiltersHeader'
import { type ImportContactsStore } from 'pages/contacts/ui/ImportContacts/store/ImportContactsStore'
import { ContactsPath } from 'pages/contacts/route/contactsPath'
import { ContactsRoutes } from 'pages/contacts/route/type'
import { type ContactsMenuStore } from 'pages/contacts/ui/ContactsMenu'
import styles from './styles.module.scss'

type IContactsContainerActionsProps = {
  contactsTableStore: ContactsTableStore
  contactsMenuStore: ContactsMenuStore
  importContactsStore: ImportContactsStore
}

export const ContactsContainerActions: FC<IContactsContainerActionsProps> = observer(
  ({ contactsTableStore, importContactsStore, contactsMenuStore }) => {
    const { usersStore, userPermissionsStore } = useEntitiesContext()
    const { checkIsImportDisabled, contactsImportModuleStore } = importContactsStore
    const { totalHistory } = contactsImportModuleStore

    const importAvailable = !usersStore.user?.isMemberRole && !usersStore.user?.isViewOnlyRole

    const onImport = () => {
      userPermissionsStore.getItem('can_create_contacts')
        ? uiStore.changeRoute({ path: `/${ContactsRoutes.root}/${ContactsRoutes.import}` })
        : new FeatureRestrictedModalStore('can_create_contacts')
    }

    const checkImportDisabling = () => {
      return checkIsImportDisabled(totalHistory)
    }

    const onAddNewContact = () => {
      userPermissionsStore.getItem('can_create_contacts')
        ? uiStore.changeRoute({ path: `/${ContactsRoutes.root}/${ContactsRoutes.new}` })
        : new FeatureRestrictedModalStore('can_create_contacts')
    }

    useEffect(() => {
      return () => {
        contactsTableStore.resetSearch()
      }
    }, [])

    return (
      <div className={styles.wrapper}>
        <ContainerActions
          padding='0'
          leftActions={[
            {
              iconButtonComponent: contactsMenuStore ? (
                <ContactsFiltersHeader contactsMenuStore={contactsMenuStore} />
              ) : null,
            },
          ]}
          rightActions={[
            {
              iconButtonComponent: (
                <div className={styles.leftActions}>
                  {importAvailable && (
                    <Button
                      text='Import'
                      icon='upload'
                      disabled={checkImportDisabling()}
                      contained='tertiary'
                      onClick={onImport}
                    />
                  )}
                  <Button text='Create' icon='add' contained='tertiary' onClick={onAddNewContact} />
                  <Dropdown
                    ariaLabel={'ContactsContainerActions'}
                    width={165}
                    icon={'moreHorizontal'}
                    placement={'bottom-start'}
                    tooltipProps={{
                      label: 'Show actions',
                      placement: 'top',
                      disableInteractive: true,
                    }}
                    iconButtonProps={{
                      size: 'medium',
                      ariaLabel: 'ContactsContainerActions_trigger',
                    }}
                    items={[
                      {
                        id: 1,
                        label: 'Import history',
                        iconL: 'clockRecent',
                        ariaLabel: 'DropdownList_ImportHistory',
                        onClick: () => {
                          userPermissionsStore.getItem('can_create_contacts')
                            ? uiStore.changeRoute({
                                path: ContactsPath.history,
                              })
                            : new FeatureRestrictedModalStore('can_create_contacts')
                        },
                      },
                      {
                        id: 2,
                        label: 'Contact cleanup',
                        iconL: 'arrowLinesHorizontal',
                        ariaLabel: 'DropdownList_ContactCleanup',
                        onClick: () => {
                          uiStore.changeRoute({
                            path: ContactsPath.cleanup,
                          })
                        },
                      },
                    ]}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>
    )
  }
)
